import { FC } from 'react';
import { Box, Typography, IconButton, Chip, Tooltip, Badge } from '@mui/material';
import { PhotoCamera, GridView, History as HistoryIcon, Add, HelpOutline, NotificationsNone } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { PageUrl } from '../../../../../types/enums/pageUrl-enums';

interface HangerHeaderProps {
    cardCount: number;
    maxCards: number;
    layoutMode: 'single' | 'double' | 'triple' | 'quad' | 'quint';
    onLayoutChange: (mode: 'single' | 'double' | 'triple' | 'quad' | 'quint') => void;
    onAddCard: () => void;
    onStartTour: () => void;
    notificationsEnabled: boolean;
    onToggleNotifications: () => void;
    generationTypeName?: string | null;
}

export const HangerHeader: FC<HangerHeaderProps> = ({
    cardCount,
    maxCards,
    layoutMode,
    onLayoutChange,
    onAddCard,
    onStartTour,
    notificationsEnabled,
    onToggleNotifications,
    generationTypeName,
}) => {
    const { t } = useTranslation();
    const history = useHistory();

    const handleLayoutToggle = () => {
        const modes: ('single' | 'double' | 'triple' | 'quad' | 'quint')[] = [
            'single',
            'double',
            'triple',
            'quad',
            'quint',
        ];
        const currentIndex = modes.indexOf(layoutMode);
        const nextIndex = (currentIndex + 1) % modes.length;
        onLayoutChange(modes[nextIndex]);
    };

    const layoutTooltipKeys: Record<HangerHeaderProps['layoutMode'], string> = {
        single: 'layoutToggleSingle',
        double: 'layoutToggleDouble',
        triple: 'layoutToggleTriple',
        quad: 'layoutToggleQuad',
        quint: 'layoutToggleQuint',
    };

    const layoutColumnCount =
        layoutMode === 'single' ? 1 : layoutMode === 'double' ? 2 : layoutMode === 'triple' ? 3 : layoutMode === 'quad' ? 4 : 5;

    return (
        <Box className="hanger-photo-upload-generate__header">
            <Box className="header-content">
                <PhotoCamera className="header-icon" />
                <Box>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                        {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.title')}
                        <Tooltip title={t('mannequin.aiGeneration.tour.helpTooltip')}>
                            <IconButton
                                className="tour-help-btn tour-start-btn"
                                onClick={onStartTour}
                                size="small"
                                aria-label={t('mannequin.aiGeneration.tour.helpTooltip')}
                                sx={{
                                    color: 'text.secondary',
                                    p: 0.25,
                                    '&:hover': {
                                        color: 'primary.main',
                                        backgroundColor: 'action.hover',
                                    },
                                }}
                            >
                                <HelpOutline sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                        {generationTypeName && (
                            <Typography
                                component="span"
                                variant="h5"
                                sx={{
                                    color: '#29b6f6',
                                    fontWeight: 500,
                                }}
                            >
                                - {generationTypeName}
                            </Typography>
                        )}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.description')}
                    </Typography>
                </Box>
            </Box>
            <Box className="header-actions">
                <Chip
                    className="header-chip"
                    label={`${cardCount} / ${maxCards}`}
                    color="primary"
                    variant="outlined"
                    size="medium"
                />
                <Tooltip
                    title={t(
                        `mannequin.aiGeneration.hangerFlow.uploadGenerate.${layoutTooltipKeys[layoutMode]}`
                    )}
                >
                    <Badge
                        badgeContent={`×${layoutColumnCount}`}
                        color="primary"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '11px',
                                fontWeight: 'bold',
                                minWidth: '24px',
                                height: '20px',
                                borderRadius: '10px',
                                zIndex: 10,
                            },
                        }}
                    >
                        <IconButton className="action-btn action-btn--secondary tour-layout-toggle" onClick={handleLayoutToggle}>
                            <GridView />
                        </IconButton>
                    </Badge>
                </Tooltip>
                <Tooltip title={t('mannequin.aiGeneration.hangerFlow.history.title')}>
                    <IconButton
                        className="action-btn action-btn--icon tour-history"
                        onClick={() => history.push(PageUrl.studioRoutes.aiGenerationHistory.url)}
                    >
                        <HistoryIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip
                    title={t(
                        notificationsEnabled
                            ? 'mannequin.aiGeneration.hangerFlow.uploadGenerate.notificationsEnabled'
                            : 'mannequin.aiGeneration.hangerFlow.uploadGenerate.notificationsDisabled'
                    )}
                >
                    <IconButton
                        className={`action-btn action-btn--secondary tour-notifications-toggle${
                            notificationsEnabled ? '' : ' action-btn--notifications-off'
                        }`}
                        onClick={onToggleNotifications}
                        aria-label={t(
                            notificationsEnabled
                                ? 'mannequin.aiGeneration.hangerFlow.uploadGenerate.notificationsEnabled'
                                : 'mannequin.aiGeneration.hangerFlow.uploadGenerate.notificationsDisabled'
                        )}
                        aria-pressed={notificationsEnabled}
                    >
                        <NotificationsNone />
                    </IconButton>
                </Tooltip>
                {/* Toplu üret butonu geçici olarak gizlendi
                {cardCount > 0 && (
                    <Tooltip title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.generateAllCards')}>
                        <span>
                            <IconButton
                                className="action-btn action-btn--secondary"
                                onClick={onGenerateAll}
                                disabled={disableGenerateAll}
                            >
                                <AutoAwesome />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                */}
                <Tooltip title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.addCard')}>
                    <span>
                        <IconButton
                            className="action-btn action-btn--primary tour-add-card"
                            onClick={onAddCard}
                            disabled={cardCount >= maxCards}
                        >
                            <Add />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
};
